import { z } from 'zod/v4'

import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

import { adminPaginationSchema } from './users.screen.js'

/*
 * «SSO»: one page of the SSO configurations of every organisation and the counts, in one answer. Each schema mirrors
 * the interface the service returns (`SsoAdminConfigResponse`, `SsoAdminStatsResponse`); the type assertions of
 * `packages/backend/src/platform/server/__tests__/shared-sso-management-screen.type-assertions.ts` tie them in both
 * directions. No row carries a certificate or a secret: the `saml` and `oidc` blocks only travel in the detail of one
 * configuration, which is not part of this screen.
 */

/** The values of the `CHECK org_sso_provider_check` (migration 0109). */
export const ssoProviderSchema = z.enum(['saml', 'oidc', 'azure', 'google', 'okta'])

export const SSO_MANAGEMENT_DEFAULT_LIMIT = 20

export const ssoConfigRowSchema = z.strictObject({
  id: z.string(),
  tenantId: z.string(),
  orgName: z.string(),
  provider: ssoProviderSchema,
  enabled: z.boolean(),
  domainMappings: z.array(z.string()),
  userCount: z.number(),
  lastTestAt: z.string().nullable(),
  lastTestSuccess: z.boolean().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const ssoStatsSchema = z.strictObject({
  overview: z.strictObject({
    totalOrgsWithSso: z.number(),
    totalEnabledConfigs: z.number(),
    totalDisabledConfigs: z.number(),
  }),
  byProvider: z.array(
    z.strictObject({
      provider: z.string(),
      total: z.number(),
      enabled: z.number(),
    }),
  ),
})

/** `status` filters by `enabled`; absent means every configuration. */
export const adminSsoManagementQuerySchema = z.strictObject({
  page: z.coerce.number<number>().int().min(1).default(1),
  limit: z.coerce.number<number>().int().min(1).max(100).default(SSO_MANAGEMENT_DEFAULT_LIMIT),
  provider: ssoProviderSchema.optional(),
  status: z.enum(['active', 'inactive']).optional(),
  orgName: z.string().min(1).max(255).optional(),
  sortBy: z.enum(['createdAt', 'updatedAt', 'orgName', 'provider']).default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
})

export const adminSsoManagementScreen = defineScreen({
  id: 'admin.ssoManagement',
  path: '/api/screens/admin/sso-management',
  access: 'super_admin',
  query: adminSsoManagementQuerySchema,
  response: z.strictObject({
    configs: screenSection(
      z.strictObject({
        items: z.array(ssoConfigRowSchema),
        pagination: adminPaginationSchema,
      }),
    ),
    stats: screenSection(ssoStatsSchema),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.sso'],
  budget: { requests: 1, statements: 6, bytes: 40_000, p95Ms: 250 },
  summary: 'SSO management: one page of SSO configurations of every organization and the counts',
})
export type SsoConfigRow = z.infer<typeof ssoConfigRowSchema>
export type SsoStats = z.infer<typeof ssoStatsSchema>
export type AdminSsoManagementQuery = ScreenQuery<typeof adminSsoManagementScreen>
export type AdminSsoManagementData = ScreenData<typeof adminSsoManagementScreen>
